<template>
  <w-sliding-popup ref="slidingPopup" title="이벤트 이력" width="800px" @close="onClose">
    <div slot="content">
      <!-- 장비 정보 -->
      <div :class="['condition-set-box', 'mr-b25', $style['condition-info-container']]">
        <ul>
          <li style="line-height: 22px;"><span class="stress" style="width:30px;">대상</span> {{ objLabel }}</li>
          <li style="line-height: 22px;"><span class="stress" style="width:30px;">기간</span> {{ periodLabel }}</li>
        </ul>
      </div>
      <!-- 이벤트 이력 정보 -->
      <el-row type="flex" justify="end">
        <w-header-search-button @click="onShowSearchToolButtonClick" align="right" class="mr-b10" />
      </el-row>
      <el-table-wrapper ref="eventTable" :data="tableData" :total="total" :columns="tableColumns" :column-default="columnDefault" :show-custom-header="true" :show-search-tool="showSearchTool" :pagination="true" @condition-change="loadData">
        <template slot-scope="scope" slot="eventlevel-slot">
          <w-eventlevel-cell :eventlevel-id="scope.row.eventlevelId" />
        </template>
        <template slot-scope="scope" slot="event-message-slot">
          <div v-if="scope.row.dynamicThresholdFlag === 'Y'" class="round-box-info-text red">
            동적
          </div>
          <span class="link c-pointer" @click="clickEventMessage(scope.row)">{{ scope.row.eventMessage }}</span>
        </template>
      </el-table-wrapper>
    </div>
  </w-sliding-popup>
</template>

<script>
import { isNil, isEmpty } from 'lodash';
import { isRequiredShowIp } from '@/utils/obj';
import { EMPTY_TEXT, getValueAfterCheckEmpty } from '@/utils/string';
import { columnDefault } from '@/constants/table';
import MsgUtil from '@/utils/message';
import { getDateToString } from '@/utils/date';
import { openFaultAnalysisPopup } from '@/utils/event';
import EventHistApi from '@/api/event/event-hist';
import WEventlevelCell from '@/components/table/EventlevelCell';

export default {
  name: 'WObjEventHistPopup',
  components: {
    WEventlevelCell,
  },
  data() {
    return {
      obj: {},
      startDatetime: null,
      endDatetime: null,
      columnDefault,
      showSearchTool: false,
      tableData: [],
      total: 0,
      tableColumns: [
        {
          prop: 'eventlevelName',
          label: '등급',
          width: 76,
          scopedSlot: 'eventlevel-slot',
          headerAlign: 'center',
          columnKey: 'eventlevelId',
          searchable: true,
          searchType: 'eventlevel',
          sortable: true,
          remoteProp: 'eventlevelId',
        },
        {
          prop: 'rsctypeName',
          label: '자원구분',
          width: 100,
          searchable: true,
          sortable: true,
        },
        {
          prop: 'eventMessage',
          label: '장애메시지',
          searchable: true,
          sortable: true,
          scopedSlot: 'event-message-slot',
        },
        {
          prop: 'checkDatetime',
          label: '발생시각',
          width: 160,
          searchable: true,
          searchType: 'date',
          sortable: true,
          formatter: this.formatterForDate,
        },
      ],
    };
  },
  computed: {
    /**
     * 대상 정보
     * @author Ju
     * @return {String}
     */
    objLabel() {
      if (isEmpty(this.obj)) {
        return EMPTY_TEXT;
      }
      return isRequiredShowIp(this.obj.objManagetypeId, this.obj.objtypeId) ? `${this.obj.objName} (${getValueAfterCheckEmpty(this.obj.ipaddress)})` : this.obj.objName;
    },
    /**
     * 조회 기간 정보
     * @author Ju
     * @return {String}
     */
    periodLabel() {
      if (isNil(this.startDatetime) || isNil(this.endDatetime)) {
        return EMPTY_TEXT;
      }
      const format = 'YYYY-MM-DD HH:mm:ss';
      return `${getDateToString(this.startDatetime, format)}  ~ ${getDateToString(this.endDatetime, format)}`;
    },
  },
  methods: {
    /**
     * 팝업을 연다.
     * @author Ju
     * @param  {Object} obj           관리대상 객체 정보
     * @param  {Date} startDatetime 조회 시작 시각
     * @param  {Date} endDatetime   조회 종료 시각
     */
    open(obj, startDatetime, endDatetime) {
      if (isEmpty(obj)) {
        this.$message(MsgUtil.getCustomResultMsgData('대상 정보가 없습니다.', false));
        return;
      }
      this.obj = obj;
      this.startDatetime = startDatetime;
      this.endDatetime = endDatetime;
      if (isNil(this.$refs.eventTable)) {
        this.$nextTick(() => {
          this.loadData();
        });
      } else {
        this.loadData();
      }
      this.$refs.slidingPopup.open();
    },
    /**
     * 이벤트 이력 정보를 조회한다.
     * @author Ju
     */
    loadData() {
      const tableParams = this.$refs.eventTable.getParameter();
      const params = {
        objId: this.obj.objId,
        startDatetime: this.startDatetime,
        endDatetime: this.endDatetime,
        ...tableParams,
      };
      this.loading = true;
      EventHistApi.getEventHistByObjAndPeriod(params)
        .then((response) => {
          if (response.data.success) {
            this.tableData = response.data.data;
            this.total = response.data.total;
          } else {
            this.$message(MsgUtil.getResultMsgData(MsgUtil.action.VIEW, '이벤트 이력 정보', false));
          }
        })
        .catch((e) => {
          this.$message(MsgUtil.getResultMsgData(MsgUtil.action.VIEW, '이벤트 이력 정보', false));
          throw new Error(e);
        })
        .finally(() => {
          this.loading = true;
        });
    },
    /**
     * 날짜 formatter
     * @author Ju
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} value  [description]
     * @return {[type]}        [description]
     */
    formatterForDate(row, column, value) {
      return getDateToString(value);
    },
    /**
     * 장애 메시지 클릭 시 해당 이벤트의 관리 대상의 성능운영 > 요약 화면으로 이동한다.
     * @author Ju
     * @param  {[type]} row [description]
     * @return {[type]}     [description]
     */
    clickEventMessage(row) {
      openFaultAnalysisPopup(row.seqNumber);
      this.$refs.slidingPopup.close();
    },
    /**
     * 헤더 검색 버튼 클릭 이벤트 핸들러
     * @author Ju
     */
    onShowSearchToolButtonClick() {
      this.showSearchTool = !this.showSearchTool;
    },
    /**
     * 팝업 닫기 이벤트 핸들러
     * @author Ju
     */
    onClose() {
      this.obj = null;
      this.startDatetime = null;
      this.endDatetime = null;
      this.tableData = [];
      this.total = 0;
      this.$refs.eventTable.clearFilter();
    },
  },
};
</script>

<style module>
.condition-info-container li {
  line-height: 20px;
}
</style>



// WEBPACK FOOTER //
// src/components/popup/FaultAnalysisPopup/popup/ObjEventHistPopup.vue